import { useState } from 'react';
import axios from 'axios';
import { X, Trash2, QrCode, Printer } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { API } from './_helpers';

export default function SitesModal({ sites, onClose, onChanged }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#0d6efd');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [qrSite, setQrSite] = useState(null);  // {site_id, name, qr_png_base64, url}

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/roster/sites`,
        { name: name.trim(), color, short_code: code.trim() || null },
        { withCredentials: true });
      setName(''); setCode(''); setColor('#0d6efd');
      onChanged();
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not save'); }
    setBusy(false);
  };

  const showQr = async (s) => {
    try {
      const r = await axios.get(`${API}/roster/clock/qr/${s.site_id}`, { withCredentials: true });
      setQrSite({ ...r.data, color: s.color, short_code: s.short_code });
    } catch (e) { alert(e?.response?.data?.detail || 'Could not load QR'); }
  };

  const printQr = () => window.print();

  const remove = async (siteId) => {
    if (!window.confirm('Remove this site? Existing shifts that use it will keep their assignment.')) return;
    try { await axios.delete(`${API}/roster/sites/${siteId}`, { withCredentials: true }); onChanged(); }
    catch (e) { alert(e?.response?.data?.detail || 'Delete failed'); }
  };

  const toggleActive = async (s) => {
    try {
      await axios.patch(`${API}/roster/sites/${s.site_id}`, { active: !s.active }, { withCredentials: true });
      onChanged();
    } catch (e) { alert('Could not update'); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-5 shadow-xl" onClick={e => e.stopPropagation()}
           data-testid="sites-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold" style={{ color: '#0d2644' }}>Sites</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Each shift can be tagged with a site. Sites colour-code the week grid and power the &ldquo;filter by site&rdquo; pills.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-2 mb-4">
          {sites.length === 0 && <div className="text-sm text-muted-foreground italic">No sites yet — add your first one below.</div>}
          {sites.map(s => (
            <div key={s.site_id} className={`flex items-center gap-2 border rounded-lg p-2 ${s.active ? '' : 'opacity-50 bg-muted/30'}`}
                 data-testid={`site-row-${s.site_id}`}>
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: s.color }} />
              <div className="flex-1 text-sm">
                <span className="font-semibold">{s.name}</span>
                {s.short_code && <span className="text-xs text-muted-foreground ml-2">({s.short_code})</span>}
              </div>
              <button onClick={() => toggleActive(s)} className="text-xs underline text-muted-foreground">
                {s.active ? 'Deactivate' : 'Activate'}
              </button>
              <button onClick={() => showQr(s)} title="Print clock-in QR for this site"
                      data-testid={`site-qr-${s.site_id}`}
                      className="p-1 text-[#0d2644] hover:bg-muted/40 rounded">
                <QrCode className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => remove(s.site_id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        <div className="border-t pt-3">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Add a site</div>
          <div className="flex gap-2 items-end flex-wrap">
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Dapto" data-testid="site-new-name" className="flex-1 min-w-[140px]" />
            <Input value={code} onChange={e => setCode(e.target.value.toUpperCase().slice(0,4))} placeholder="DAP" data-testid="site-new-code" className="w-20" />
            <label className="text-xs font-semibold text-muted-foreground">
              <span className="block mb-1">Colour</span>
              <input type="color" value={color} onChange={e => setColor(e.target.value)} data-testid="site-new-color"
                     className="h-9 w-12 border rounded cursor-pointer" />
            </label>
            <button onClick={add} disabled={!name.trim() || busy} data-testid="site-new-save"
              className="text-sm font-semibold px-4 py-2 rounded-lg bg-[#0d2644] text-white disabled:opacity-50">
              Add site
            </button>
          </div>
          {err && <div className="text-xs text-red-600 mt-2">{err}</div>}
        </div>
      </div>

      {qrSite && (
        <div className="fixed inset-0 bg-black/75 z-[60] flex items-center justify-center p-4 print:bg-white print:p-0"
             onClick={() => setQrSite(null)}
             data-testid="qr-overlay">
          <div onClick={e => e.stopPropagation()}
               className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl text-center print:shadow-none print:rounded-none">
            <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: '#0d2644' }}>
              i3 Radiology · Clock in / out
            </div>
            <h3 className="text-2xl font-bold mb-1" style={{ color: '#0d2644' }}>{qrSite.site_name}</h3>
            {qrSite.short_code && (
              <div className="inline-block text-xs font-semibold px-2 py-0.5 rounded mb-3" style={{ background: qrSite.color, color: '#fff' }}>
                {qrSite.short_code}
              </div>
            )}
            <img src={qrSite.qr_png_base64} alt={`Clock-in QR for ${qrSite.site_name}`}
                 className="mx-auto w-64 h-64 my-2"
                 data-testid="qr-image" />
            <div className="text-sm font-bold mt-3" style={{ color: '#0d2644' }}>
              Scan with your phone camera to clock in or out.
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Open the link with your i3 portal login.
            </div>
            <div className="mt-5 flex items-center justify-center gap-2 print:hidden">
              <button onClick={() => setQrSite(null)} data-testid="qr-close"
                      className="text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-muted/40">
                Close
              </button>
              <button onClick={printQr} data-testid="qr-print"
                      className="text-sm font-semibold px-4 py-2 rounded-lg bg-[#0d2644] text-white inline-flex items-center gap-1.5">
                <Printer className="w-4 h-4" /> Print
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
